import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { postNotification } from "../../components/utilities/Notifications";
import {
  getGlobalRegion,
  getRegion,
  getRegionsArray
} from "../../components/api/APIUtilities";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import {
  findGetParameter,
  getData
} from "../../components/utilities/UtilityFunctions";
import { useNavigate, useParams } from "react-router";
import LeaderboardsOverview from "./LeaderboardsOverview";
import LeaderboardsTruncated from "./LeaderboardsTruncated";
import {
  BCPAngleDown,
  BCPArrowLeft,
  BCPShareNodes
} from "../../components/icons/bcp/BCPIcons";
import moment from "moment";
import LeaderboardComponent from "./LeaderboardsComponent";
import ShareModal from "../../components/modals/ShareModal";
import {
  getCookie,
  getLocalStorage,
  hasCookiePermission,
  setCookie,
  setLocalStorage
} from "../../components/layout/cookies/CookieFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const defaultRegion = getEnvironmentVariable("DEFAULT_RANKINGS_REGION") || {
  id: "VgQKgqmTPU",
  name: "United States"
};
const globalRegion = getGlobalRegion();

// Want a parent component to pass in regionId and regions array. Don't render the component if there are no regions.
export default function Leaderboards(props) {
  const { organization } = props;

  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("", { keyPrefix: "pages.leaderboards" });

  // --- League ---
  const urlParams = useParams();
  const leagueId =
    urlParams?.leagueId ||
    urlParams?.league ||
    findGetParameter("league") ||
    findGetParameter("leagueId");
  const urlRegionId = findGetParameter("regionId");
  const armyId =
    findGetParameter("army") ||
    findGetParameter("faction") ||
    urlParams?.armyId ||
    urlParams?.faction ||
    urlParams?.army;

  const [league, setLeague] = useState(props?.league);
  const [prevLeague, setPrevLeague] = useState(null);

  // --- Regions ---
  const [regions, setRegions] = useState(
    props?.regions?.length ? props.regions : null
  );
  const [disableRegionPicker, setDisableRegionPicker] = useState(
    Boolean(league || leagueId)
  );

  const [region, setRegion] = useState(null);
  const [childRegion, setChildRegion] = useState(null);
  const [subChildRegion, setSubChildRegion] = useState(null);

  const [childRegions, setChildRegions] = useState(null);
  const [subChildRegions, setSubChildRegions] = useState(null);

  // --- General Vars ---
  const embed =
    props?.embed || props?.appRoute || findGetParameter("embed") === "true";
  const organizationId = urlParams?.organizationId || props?.organizationId;

  const hideRegionPicker =
    props?.hideRegionPicker ||
    findGetParameter("hideRegionPicker") === "true" ||
    false;
  const hideNav =
    findGetParameter("hideNav") === "true" || props?.hideNav || false;

  // --- UseState Vars ---
  const [activeTab, setActiveTab] = useState(
    props?.activeTab === "players" && armyId
      ? "army"
      : props?.activeTab || "overview"
  );
  const [loading, setLoading] = useState(true);
  const [gameStore, setGameStore] = useState(null);
  const [army, setArmy] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [includeRegion, setIncludeRegion] = useState(false);

  const fetchGameStore = async () => {
    const apiGameStore = await getData(`/gamestores/${organizationId}`);
    return apiGameStore;
  };

  const fetchDefaultRegion = async (_regions) => {
    try {
      const regionId =
        urlRegionId ||
        props?.regionId ||
        getCookie("rankingsRegion") ||
        league?.defaultRegion?.id ||
        defaultRegion?.id;

      const apiData =
        league || leagueId
          ? await getData(`/leagues/${league?.id || leagueId}/defaultregion`)
          : null;

      const tempRegions = _regions || regions;
      const _region = getRegion(apiData?.id || regionId, tempRegions);

      if (tempRegions?.length) {
        // Loops through every region for a matching region, sets the appropriate region type if found
        tempRegions.forEach((parentReg) => {
          if (parentReg?.id === _region?.id) {
            setRegion(parentReg);
          } else if (parentReg?.children?.length) {
            parentReg.children.forEach((child) => {
              if (child?.id === _region?.id) {
                setChildRegion(child);
              } else if (child?.children?.length) {
                child?.children?.forEach((subChild) => {
                  if (subChild?.id === _region?.id) {
                    setSubChildRegion(subChild);
                  }
                });
              }
            });
          }
        });
      }

      setCookie("rankingsRegion", apiData?.id || regionId);
    } catch (err) {
      if (!region) {
        setRegion(defaultRegion);
      }
    }
  };

  const fetchArmy = async () => {
    try {
      const apiArmy = await getData(`/armies/${armyId}`);
      setArmy(apiArmy);
    } catch (err) {
      postNotification("error", err);
    }
  };

  const fetchRegions = async (_leagueId) => {
    let _regions = [];

    try {
      setDisableRegionPicker(true);
      let apiRegions = [];

      if (_leagueId) {
        apiRegions = await getData(`/leagues/${_leagueId}/regions`);
      } else if (organizationId) {
        apiRegions = await getData(`/gamestores/${organizationId}/regions`);
      }

      if (!apiRegions?.length) {
        _regions = getRegionsArray();
      } else if (apiRegions?.length === 1) {
        _regions = league?.gw_itc
          ? [
              ...apiRegions[0]?.children,
              { id: apiRegions?.[0]?.id, name: apiRegions?.[0]?.name }
            ]
          : [
              { id: apiRegions?.[0]?.id, name: apiRegions?.[0]?.name },
              ...apiRegions?.[0]?.children
            ];
      } else {
        _regions = league?.gw_itc
          ? apiRegions.sort((a, b) => {
              if (a?.id === globalRegion?.id) return 1;
              if (b?.id === globalRegion?.id) return -1;
              return 0;
            })
          : apiRegions;
      }
    } catch (err) {
      postNotification("error", err);
    } finally {
      if (loading && regions) setLoading(false);
      setDisableRegionPicker(false);
      return _regions;
    }
  };

  // Update the child regions when the parent region changes
  useEffect(() => {
    const tempRegion = subChildRegion || childRegion || region;

    const handleRegionChange = async () => {
      try {
        setLoading(true);

        let _gameStore = gameStore;
        if (organization && !_gameStore) {
          _gameStore = await fetchGameStore();

          if (_gameStore) {
            setGameStore(_gameStore);
          }
        }

        // Sets the regions to their appropriate region types
        if (regions) {
          if (subChildRegion) {
            regions.forEach((parentReg) => {
              if (parentReg?.children?.length) {
                // Loops through every parent region for a matching child region
                parentReg.children.forEach((child) => {
                  if (child?.children?.length) {
                    child?.children?.forEach((subChild) => {
                      if (subChild?.id === subChildRegion?.id) {
                        setSubChildRegions(child.children);
                        setChildRegions(parentReg.children);
                        if (region?.id !== parentReg?.id)
                          setRegion(getRegion(parentReg?.id, regions));
                        if (childRegion?.id !== child?.id)
                          setChildRegion(getRegion(child.id, regions));
                      }
                    });
                  }
                });
              }
            });
          } else if (childRegion) {
            regions.forEach((parentReg) => {
              if (parentReg?.children?.length) {
                // Loops through every parent region for a matching child region
                parentReg.children.forEach((child) => {
                  if (child?.id === childRegion?.id) {
                    setChildRegions(parentReg.children);
                    // If region does not match parentReg?.id, set the region to the parent
                    if (region?.id !== parentReg?.id) {
                      setRegion(getRegion(parentReg?.id, regions));
                    }
                    setSubChildRegions(child?.children);
                    setSubChildRegion(null);
                  }
                });
              }
            });
          } else {
            setRegion(getRegion(tempRegion?.id, regions));
            setChildRegion(null);
            setSubChildRegion(null);
            setChildRegions(tempRegion?.children || null);
            setSubChildRegions(tempRegion?.children?.children || null);
          }
        }
      } catch (err) {
        postNotification("error", err);
      } finally {
        setLoading(false);
      }
    };

    if (tempRegion) handleRegionChange();
    // eslint-disable-next-line
  }, [region, childRegion, subChildRegion, regions]);

  // Fetch the default region for the league if it exists and if it hasn't been fetched in the last 24 hours
  useEffect(() => {
    if (armyId && !army) {
      fetchArmy();
    }

    const pageSetup = async () => {
      const apiRegions = await fetchRegions(league?.id || leagueId);
      setRegions(apiRegions);

      const shouldFetchDefaultRegion = () => {
        if (!hasCookiePermission("defaultRegionLastFetched")) return true;
        const lastFetched = getLocalStorage("defaultRegionLastFetched");
        if (!lastFetched) return true;

        const daysSinceLastFetch = moment().diff(moment(lastFetched), "days");
        return daysSinceLastFetch >= 1;
      };

      const getDefaultRegion = () => {
        const regionId =
          urlRegionId ||
          props?.regionId ||
          getCookie("rankingsRegion") ||
          defaultRegion?.id;
        return getRegion(regionId, apiRegions) || defaultRegion;
      };

      if (league || leagueId) {
        if (shouldFetchDefaultRegion()) {
          fetchDefaultRegion(apiRegions);
          setLocalStorage(
            "defaultRegionLastFetched",
            moment().format("YYYY-MM-DD HH:mm:ss")
          );
        } else {
          setRegion(getDefaultRegion());
        }
      } else if (!region) {
        fetchDefaultRegion(apiRegions);
      }
    };

    pageSetup();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setDisableRegionPicker(true);
    setLoading(true);

    const go = async () => {
      if (league || leagueId || !regions) {
        const lastFetched = getLocalStorage("defaultRegionLastFetched");

        const apiRegions = await fetchRegions(league?.id || leagueId);
        setRegions(apiRegions);

        if (
          !hasCookiePermission("defaultRegionLastFetched") ||
          (hasCookiePermission("defaultRegionLastFetched") &&
            (!lastFetched ||
              (lastFetched && moment().diff(moment(lastFetched)), "days") >= 1))
        ) {
          fetchDefaultRegion();
          if (league || leagueId) {
            setLocalStorage(
              "defaultRegionLastFetched",
              moment().format("YYYY-MM-DD HH:mm:ss")
            );
          }
        }
      } else {
        setRegions(props?.regions?.length ? props.regions : getRegionsArray());
      }
    };

    go();

    setDisableRegionPicker(false);
    setLoading(false);
    // eslint-disable-next-line
  }, [league, leagueId]);

  return (
    <Box sx={{ width: "100%" }}>
      {loading && <ModalLoadingFull />}

      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid
          container
          spacing={2}
          sx={{
            p: 2,
            "@media (max-width: 599px)": {
              paddingRight: "3px"
            }
          }}
        >
          {/* Navigation */}
          <Grid item xs={12} md={4}>
            {activeTab !== "overview" &&
              !hideNav &&
              (gameStore || organizationId || league) && (
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "left",
                    color: "link.main",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    pr: 2,
                    cursor: "pointer"
                  }}
                  onClick={() => {
                    if (activeTab === "truncated") {
                      setActiveTab("overview");
                      navigate(
                        `/${
                          gameStore?.id || organizationId
                            ? `organization/leaderboards/${
                                gameStore?.id || organizationId
                              }`
                            : "leaderboards"
                        }${embed ? "?embed=true" : ""}`
                      );
                      setLeague(null);
                    } else {
                      setActiveTab("truncated");
                      if (activeTab === "teams" && !prevLeague && league) {
                        setLeague(null);
                      } else if (prevLeague) {
                        setLeague(prevLeague);
                        setPrevLeague(null);
                      }
                      navigate(
                        `/${
                          gameStore?.id || organizationId
                            ? `organization/leaderboards/${
                                gameStore?.id || organizationId
                              }`
                            : "leaderboards"
                        }/${prevLeague?.id || leagueId || league?.id}${
                          embed ? "?embed=true" : ""
                        }`
                      );
                    }
                    window.scrollTo(0, 0);
                  }}
                >
                  <BCPArrowLeft
                    sx={{
                      color: theme?.palette?.link.main
                    }}
                  />
                  {` ${t("backTo")} ${
                    activeTab === "truncated"
                      ? `${
                          gameStore?.id === "OlkMrRfxrN" ||
                          organizationId === "OlkMrRfxrN"
                            ? "ITC"
                            : gameStore?.id === "12GNHWBA74" ||
                                organizationId === "12GNHWBA74"
                              ? "Unplug Yourself"
                              : gameStore?.name
                                ? gameStore.name
                                : "BCP"
                        } Rankings`
                      : activeTab === "players" ||
                          activeTab === "teams" ||
                          activeTab === "army"
                        ? `${
                            prevLeague?.name ||
                            league?.name ||
                            t("leagueRankings")
                          }`
                        : t("rankings")
                  }`}
                </Typography>
              )}
          </Grid>

          {/* Region Pickers */}
          {!hideRegionPicker && region && regions && (
            <Hidden mdUp>
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ textAlign: "center" }}>
                  {t("yourRegion")}
                </Typography>
              </Grid>
            </Hidden>
          )}

          <Grid item xs={12} md={8}>
            <Hidden mdDown>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  mb: !hideRegionPicker && region && regions ? 1 : 0
                }}
              >
                {!hideRegionPicker && region && regions && (
                  <Typography variant="h4">{t("yourRegionColon")}</Typography>
                )}
                <Tooltip title={t("share")}>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShareModalOpen(true)}
                  >
                    <BCPShareNodes style={{ width: "20px" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Hidden>

            {!hideRegionPicker && region && regions && (
              <Grid
                container
                spacing={1}
                sx={{
                  flexDirection: ["column-reverse", "row-reverse"],
                  "& .MuiGrid-item": {
                    "@media (max-width: 599px)": {
                      paddingRight: "14px"
                    }
                  }
                }}
              >
                {subChildRegions?.length ? (
                  <Grid item xs={12} md={4}>
                    <RegionPicker
                      region={subChildRegion || null}
                      setSubChildRegion={setSubChildRegion}
                      regions={subChildRegions}
                      label={t("subRegion")}
                      setCookies={setCookie}
                      allowClear
                      disabled={disableRegionPicker}
                      mode="subchild"
                      t={t}
                    />
                  </Grid>
                ) : (
                  <></>
                )}

                {childRegions?.length ? (
                  <Grid item xs={12} md={4}>
                    <RegionPicker
                      region={childRegion || null}
                      setChildRegion={setChildRegion}
                      regions={childRegions}
                      label={t("subRegion")}
                      subChildRegion={subChildRegion}
                      setSubChildRegion={setSubChildRegion}
                      setCookies={setCookie}
                      allowClear
                      disabled={disableRegionPicker}
                      mode="child"
                      t={t}
                    />
                  </Grid>
                ) : (
                  <></>
                )}

                {regions?.length ? (
                  <Grid item xs={12} md={4}>
                    <RegionPicker
                      region={region}
                      setRegion={setRegion}
                      childRegion={childRegion}
                      setChildRegion={setChildRegion}
                      subChildRegion={subChildRegion}
                      setSubChildRegion={setSubChildRegion}
                      regions={regions}
                      label={t("region")}
                      setCookies={setCookie}
                      disabled={disableRegionPicker}
                      mode="parent"
                      t={t}
                    />
                  </Grid>
                ) : (
                  <></>
                )}
              </Grid>
            )}

            <Hidden mdUp>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Tooltip title={t("share")}>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShareModalOpen(true)}
                  >
                    <BCPShareNodes style={{ width: "20px" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Hidden>
          </Grid>

          {/* Title */}
          <Grid item xs={12}>
            {(activeTab === "overview" ||
              gameStore ||
              organizationId ||
              gameStore ||
              league) && (
              <Typography
                variant="h2"
                component="h3"
                gutterBottom
                sx={{ textAlign: "left", fontWeight: "700" }}
              >
                {(gameStore?.id === "OlkMrRfxrN" ||
                  organizationId === "OlkMrRfxrN") &&
                activeTab === "overview"
                  ? "ITC "
                  : gameStore?.id === "12GNHWBA74" ||
                      organizationId === "12GNHWBA74"
                    ? "Unplug Yourself "
                    : gameStore?.name && activeTab === "overview"
                      ? gameStore.name + " - "
                      : activeTab === "teams" && prevLeague?.name
                        ? prevLeague.name + " - "
                        : activeTab === "army" && army?.name
                          ? army.name + " - "
                          : league?.name
                            ? league.name + " - "
                            : "BCP "}
                {league?.teams ? t("teamRankings").split(" ")[0] + " " : ""}
                {t("rankings")}
              </Typography>
            )}
          </Grid>

          {/* Page */}
          {activeTab === "overview" && (
            <LeaderboardsOverview
              region={
                subChildRegion
                  ? subChildRegion
                  : childRegion
                    ? childRegion
                    : region
              }
              organizationId={organizationId}
              setLeague={setLeague}
              setActiveTab={setActiveTab}
              loading={loading}
              embed={embed}
            />
          )}
          {activeTab === "truncated" && (
            <LeaderboardsTruncated
              region={
                subChildRegion
                  ? subChildRegion
                  : childRegion
                    ? childRegion
                    : region
              }
              league={league}
              setLeague={setLeague}
              setPrevLeague={setPrevLeague}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              embed={embed}
            />
          )}
          {(activeTab === "players" ||
            activeTab === "teams" ||
            activeTab === "army") && (
            <LeaderboardComponent
              activeTab={activeTab}
              region={
                subChildRegion
                  ? subChildRegion
                  : childRegion
                    ? childRegion
                    : region
              }
              regions={regions}
              league={league}
              setLeague={setLeague}
              embed={embed}
              gameStore={gameStore}
              setGameStore={setGameStore}
              setArmy={setArmy}
            />
          )}
        </Grid>
      </Container>

      <ShareModal
        parentOpen={shareModalOpen}
        setParentOpen={(val) => {
          setShareModalOpen(val);
          if (!val) setIncludeRegion(false);
        }}
        url={(() => {
          const activeRegion = subChildRegion || childRegion || region;
          const base = window.location.origin + window.location.pathname;
          const params = new URLSearchParams(window.location.search);
          params.delete("regionId");
          if (includeRegion && activeRegion?.id) {
            params.set("regionId", activeRegion.id);
          }
          const queryString = params.toString();
          return queryString ? `${base}?${queryString}` : base;
        })()}
        linkOnly
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={includeRegion}
              onChange={(e) => setIncludeRegion(e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">{t("includeRegion")}</Typography>}
        />
      </ShareModal>
    </Box>
  );
}

function RegionPicker(props) {
  const {
    setRegion,
    label,
    regions,
    setCookies,
    setChildRegion,
    allowClear,
    disabled,
    mode,
    setSubChildRegion,
    t
  } = props;

  const [regionsMenu, setRegionsMenu] = useState(null);
  const [menuWidth, setMenuWidth] = useState("200px");

  return (
    <>
      <Button
        variant={props?.region?.name ? "contained" : "outlined"}
        onClick={async (e) => {
          setRegionsMenu(e.currentTarget);
          setMenuWidth(e.currentTarget.clientWidth);
        }}
        disabled={disabled}
        endIcon={
          <BCPAngleDown
            style={{
              transform: `rotate(${Boolean(regionsMenu) ? "-180" : 0}deg)`,
              transition: "transform 0.2s"
            }}
          />
        }
        fullWidth
      >
        {props?.region?.name || label || t("region")}
      </Button>

      {regions?.length && (
        <Menu
          id="region-menu"
          anchorEl={regionsMenu}
          open={Boolean(regionsMenu) && !disabled && regions?.length}
          disableScrollLock
          onClose={() => {
            setRegionsMenu(null);
          }}
          disableAutoFocusItem
        >
          {allowClear && (
            <>
              <MenuItem
                onClick={() => {
                  if (mode === "child") {
                    setChildRegion(null);
                    setSubChildRegion(null);
                  } else {
                    setSubChildRegion(null);
                  }
                  setRegionsMenu(null);
                }}
                sx={{ width: menuWidth }}
                value=""
              >
                <em>{t("clear")}</em>
              </MenuItem>
              <Divider />
            </>
          )}
          {regions &&
            regions?.map((reg) => (
              <>
                <MenuItem
                  key={reg?.id}
                  value={reg?.id}
                  selected={reg?.id === props?.region?.id}
                  onClick={async () => {
                    if (mode === "parent") {
                      setChildRegion(null);
                      setSubChildRegion(null);
                      setRegion(getRegion(reg?.id, regions));
                    } else if (mode === "child") {
                      setChildRegion(getRegion(reg?.id, regions));
                      setSubChildRegion(null);
                    } else {
                      setSubChildRegion(getRegion(reg?.id, regions));
                    }

                    setCookies("rankingsRegion", reg?.id);

                    setRegionsMenu(null);
                  }}
                  sx={{ width: menuWidth }}
                >
                  {reg?.name}
                </MenuItem>
                {reg?.name === t("global") && <Divider />}
              </>
            ))}
        </Menu>
      )}
    </>
  );
}
